import { t } from '../../i18n';
import { CustomerDetail } from '../../types/customer';
import { PaymentListItem } from '../../types/payment';
import { formatDate, formatMoney } from '../../utils/format';
import {
  dateCell,
  downloadCustomerExcel,
  escapeHtml,
  excelHeaderRowNumber,
  excelSubtotal,
  htmlDataRow,
  htmlSummaryCard,
  moneyCell,
  printCustomerPdf,
} from './exportCustomerShared';

function paymentHeaders(): string[] {
  return [
    t().payments.paymentDate,
    t().payments.invoice,
    t().financial.invoice,
    t().financial.cash,
    t().payments.paymentTotal,
  ];
}

function isNumeric(index: number): boolean {
  return index >= 2;
}

function paymentInvoiceLabel(payment: PaymentListItem): string {
  if (payment.isMultiAllocated) {
    const numbers = payment.allocations
      .map((row) => row.invoiceNo)
      .filter(Boolean)
      .join(', ');
    return numbers
      ? `${t().payments.multiAllocated}: ${numbers}`
      : t().payments.multiAllocated;
  }
  if (payment.invoice) return payment.invoice.invoiceNo;
  if (payment.allocations[0]?.invoiceNo) {
    return payment.allocations[0].invoiceNo;
  }
  return t().payments.unallocated;
}

export function exportCustomerPaymentsExcel(
  customer: CustomerDetail,
  payments: PaymentListItem[],
): void {
  const labels = t().customers;
  const headers = paymentHeaders();
  let invoice = 0;
  let cash = 0;
  let total = 0;
  for (const payment of payments) {
    invoice += Number(payment.totals.invoice);
    cash += Number(payment.totals.cash);
    total += Number(payment.totals.total);
  }
  const totals = {
    invoice: invoice.toFixed(2),
    cash: cash.toFixed(2),
    total: total.toFixed(2),
  };
  const summaryRows = [
    [
      { type: 'empty' as const },
      { type: 'string' as const, value: t().financial.invoice, style: 'Section' },
      { type: 'string' as const, value: t().financial.cash, style: 'Section' },
      {
        type: 'string' as const,
        value: t().financial.grandTotal,
        style: 'Section',
      },
    ],
    [
      { type: 'string' as const, value: labels.totalPayments, style: 'Label' },
      moneyCell(totals.invoice, 'MoneyBold'),
      moneyCell(totals.cash, 'MoneyBold'),
      moneyCell(totals.total, 'MoneyBold'),
    ],
  ];
  const headerRow = excelHeaderRowNumber(summaryRows.length);
  const firstDataRow = headerRow + 1;
  const lastDataRow =
    payments.length > 0 ? headerRow + payments.length : headerRow;

  downloadCustomerExcel({
    customer,
    filename: `pliromes-${customer.id}.xls`,
    sheetTitle: labels.tabs.payments,
    columnWidths: [78, 160, 82, 82, 88],
    columnCount: 5,
    headers,
    isNumericHeader: isNumeric,
    summaryRows,
    dataRows: payments.map((payment) => [
      dateCell(payment.paymentDate),
      { type: 'string', value: paymentInvoiceLabel(payment) },
      moneyCell(payment.totals.invoice),
      moneyCell(payment.totals.cash),
      moneyCell(payment.totals.total),
    ]),
    totalRow: [
      { type: 'string', value: t().common.total, style: 'TotalLabel' },
      { type: 'empty', style: 'TotalEmpty' },
      excelSubtotal(3, firstDataRow, lastDataRow),
      excelSubtotal(4, firstDataRow, lastDataRow),
      excelSubtotal(5, firstDataRow, lastDataRow),
    ],
  });
}

export function exportCustomerPaymentsPdf(
  customer: CustomerDetail,
  payments: PaymentListItem[],
): void {
  const labels = t().customers;
  const headers = paymentHeaders();
  let invoice = 0;
  let cash = 0;
  let total = 0;
  for (const payment of payments) {
    invoice += Number(payment.totals.invoice);
    cash += Number(payment.totals.cash);
    total += Number(payment.totals.total);
  }
  const totals = {
    invoice: invoice.toFixed(2),
    cash: cash.toFixed(2),
    total: total.toFixed(2),
  };

  printCustomerPdf({
    customer,
    title: labels.tabs.payments,
    summaryHtml: `<div class="summary">${htmlSummaryCard(
      labels.totalPayments,
      totals.invoice,
      totals.cash,
      totals.total,
    )}</div>`,
    headers,
    isNumeric,
    items: payments,
    rowHtml: (payment) =>
      htmlDataRow(
        [
          formatDate(payment.paymentDate),
          paymentInvoiceLabel(payment),
          formatMoney(payment.totals.invoice),
          formatMoney(payment.totals.cash),
          formatMoney(payment.totals.total),
        ],
        isNumeric,
      ),
    totalsRowHtml: `
      <tr>
        <td colspan="2">${escapeHtml(t().common.total)}</td>
        <td class="num">${escapeHtml(formatMoney(totals.invoice))}</td>
        <td class="num">${escapeHtml(formatMoney(totals.cash))}</td>
        <td class="num">${escapeHtml(formatMoney(totals.total))}</td>
      </tr>`,
  });
}
